import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  viewChild,
} from '@angular/core';

import { contrastColor } from '@plugins/client/colors/color-utils';
import { FocusOutsideDirective } from '@plugins/client/overlay/focus-outside.directive';
import { Maybe, Nullish } from '@plugins/shared/types';

import { DEFAULT_AVATAR_COLOR, TRANSLATION_KEYS } from '../../private/config';
import { ValidSearchTypes } from '../../private/utils/types.private';

const { AVATAR_DISPLAY_LIST } = TRANSLATION_KEYS;

/** Display group avatar with option to view list of entities or avatar
 * of an entity if only one is provided.
 *
 * Example:
 * ```html
 * <plugins-avatar-display-list [userIds]="ids" [listSize]="listSize" />
 * ``` */
@Component({
  selector: 'plugins-avatar-display-list',
  standalone: false,
  templateUrl: './avatar-display-list.component.html',
  styleUrl: './avatar-display-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AvatarDisplayListComponent {
  readonly userIds = input<string[]>([]);

  /** Determines if the title prop should display entity details. Defaults to `false` */
  readonly detailedTitles = input<Maybe<boolean>>();
  /** Background color of the group icon. The icon's fill is white or black
   * depending on contrast evaluation  */
  readonly color = input<Maybe<string>>();

  /** Passed to the `UserListComponent` size input. */
  readonly listSizing = input<Maybe<number | string>>('');
  /** Should close button be shown when `UserListComponent` is opened. */
  readonly showListClose = input(false);
  /** Can be used for fetch optimization or narrowing when making calls to the `UserDetailsService`.
   *
   * Defaults to `all`. */
  readonly searchType = input<ValidSearchTypes>('all');

  /** Should `UserListComponent` implement display limiting
   * and partial loading? Defaults to `true`. */
  readonly displayLimit = input(true);
  /** Shoul animations in `UserLIstComponent` be disabled? Defaults to `false` */
  readonly disableAnimations = input(false);
  /** HIghligh color for the load more button in `UserListComponent` */
  readonly highlightColor = input<string | undefined, Maybe<string>>(
    undefined,
    {
      transform: (val: Maybe<string>) => {
        return val === null ? undefined : val;
      },
    },
  );
  readonly loadLimit = input<number | Nullish>();
  /** Should entities link to user profile page. Defaults to `true` */
  readonly profileLinksEnabled = input(true);

  readonly focusDirective = viewChild(FocusOutsideDirective);

  protected readonly colors = computed(() => {
    const bgColor = this.color() || DEFAULT_AVATAR_COLOR;
    const fillColor = contrastColor(bgColor);
    return { bgColor, fillColor };
  });

  buttonTranslations = AVATAR_DISPLAY_LIST;

  onToggleList(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.focusDirective()?.toggle();
  }
}
